import * as React from 'react';

import type { CommentReply } from '../../../../ui/tiptap/extensions/comment-mark';
import { cn } from '@cotera/client/v0/lib/utils';
import { formatCommentTime } from './comment-time';

/**
 * The first letter of someone's first name.
 *
 * One letter, not two. The avatar is a 16px circle sitting beside the person's full
 * name, which is already right there and says everything a second initial would — and
 * a name someone signed up with as a single word gave a lopsided one-letter badge next
 * to everyone else's two.
 */
function initial(name: string): string {
  const first = name.trim().split(/\s+/).filter(Boolean)[0];
  if (first === undefined) {
    return '?';
  }
  // `[...]` rather than `[0]`, so a name starting with an emoji or any character
  // outside the basic plane gives its whole glyph instead of half a surrogate pair.
  return ([...first][0] ?? '?').toUpperCase();
}

/**
 * Whoever wrote — or is about to write — a message, as a circle with their initial.
 *
 * Shared by the bylines over posted messages and by the composer they are written in,
 * so the avatar that appears the moment you start typing is the one that ends up
 * beside what you said.
 */
export function CommentAvatar({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-medium text-muted-foreground',
        className
      )}
    >
      {initial(name)}
    </div>
  );
}

/**
 * Who said it and when, with whatever they said passed as children.
 *
 * The one byline in the feature. The index's rows and the cards against the document
 * had a copy each, which is how they ended up telling the time differently — the rows
 * in dates, the cards in "9m" — for the same message. `compact` is the only thing that
 * varies now: a row in a list is a glance, a message in a card is being read.
 */
export function CommentByline({
  reply,
  compact = false,
  children,
}: {
  reply: CommentReply;
  compact?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex gap-2">
      <CommentAvatar name={reply.authorName} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <span
            className={cn(
              'truncate font-medium',
              compact ? 'text-xs' : 'text-sm'
            )}
          >
            {reply.authorName}
          </span>
          <span
            className={cn(
              'shrink-0 text-muted-foreground',
              compact ? 'text-[10px]' : 'text-[11px]'
            )}
          >
            {formatCommentTime(reply.createdAt)}
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}
